import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Star, Quote, Loader2, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import JsonLd from "@/components/JsonLd";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_REVIEWS_URL = "https://share.google/tZwmSDnoxsFBMdHBM";

// Avis réel retiré de l'accueil à la demande du client (avis anglais d'une seule ligne).
const EXCLUDED_TEXTS = ["Great service and amazing job"];

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}

// Simple inline Google "G" logo (multi-color)
function GoogleLogo({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

export default function ReviewsSection({ limit }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const trackRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/reviews`);
        setReviews(res.data || []);
      } catch (e) {
        /* silent */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Seuls les vrais avis Google affichés (l'avis anglais d'une ligne est exclu sur demande du client).
  const filtered = reviews.filter((r) => !EXCLUDED_TEXTS.some((x) => (r.text || "").startsWith(x)));
  const shown = limit ? filtered.slice(0, limit) : filtered;

  const average =
    filtered.length > 0
      ? (filtered.reduce((s, r) => s + r.rating, 0) / filtered.length).toFixed(1)
      : null;

  // Balisage structuré construit à partir des avis réellement affichés sur la page.
  const reviewsLd =
    shown.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "@id": "https://reflectumpellicule.com/#business",
          name: "Reflectum Pellicule",
          url: "https://reflectumpellicule.com",
          telephone: "+1-438-255-6438",
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: average,
            reviewCount: filtered.length,
          },
          review: shown.map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.name },
            reviewBody: r.text,
            reviewRating: {
              "@type": "Rating",
              ratingValue: r.rating,
              bestRating: 5,
              worstRating: 1,
            },
          })),
        }
      : null;

  const scroll = (dir) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * (el.clientWidth * 0.8), behavior: "smooth" });
  };

  return (
    <section data-testid="reviews-section" className="py-16 sm:py-24 lg:py-32 bg-slate-950 text-white border-t border-white/5">
      <JsonLd id="reviews" data={reviewsLd} />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 lg:mb-12">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-[#C5A880]">{t("reviews.eyebrow")}</div>
            <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight leading-[1.1] mt-3">
              {t("reviews.title")}
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            {average && (
              <div className="flex items-center gap-3">
                <div className="flex">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Star key={i} className={`w-5 h-5 ${i < Math.round(Number(average)) ? "fill-[#EAB308] text-[#EAB308]" : "text-slate-600"}`} />
                  ))}
                </div>
                <a
                  href={GOOGLE_REVIEWS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="google-rating-badge"
                  className="text-sm text-slate-300 font-medium hover:text-[#C5A880] transition-colors"
                >
                  {average}/5 · {t("reviews.onGoogle")}
                </a>
              </div>
            )}
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-review-link"
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white border border-slate-200 text-slate-900 font-medium shadow-sm hover:shadow-md transition-all"
            >
              <GoogleLogo className="w-5 h-5" />
              {t("reviews.leaveReview")}
              <ExternalLink className="w-4 h-4 text-slate-500" />
            </a>
            <div className="hidden sm:flex gap-2">
              <button
                data-testid="reviews-prev"
                onClick={() => scroll(-1)}
                aria-label="Avis précédent"
                className="w-11 h-11 rounded-full border border-white/15 bg-white/5 text-white grid place-items-center hover:bg-white/10 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                data-testid="reviews-next"
                onClick={() => scroll(1)}
                aria-label="Avis suivant"
                className="w-11 h-11 rounded-full border border-white/15 bg-white/5 text-white grid place-items-center hover:bg-white/10 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : shown.length === 0 ? (
          <div data-testid="reviews-empty" className="rounded-2xl bg-white/5 border border-white/10 p-10 lg:p-16 text-center">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-white/5 border border-white/10 grid place-items-center">
              <GoogleLogo className="w-10 h-10" />
            </div>
            <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mt-6">
              {t("reviews.emptyTitle")}
            </h3>
            <p className="mt-3 text-slate-300 max-w-lg mx-auto">
              {t("reviews.emptyText")}
            </p>
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-review-cta"
              className="inline-flex items-center gap-3 mt-8 px-8 py-4 rounded-full bg-white text-slate-900 font-medium hover:bg-blue-100 transition-colors"
            >
              <GoogleLogo className="w-5 h-5" />
              {t("reviews.emptyCta")}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        ) : (
          <div
            ref={trackRef}
            data-testid="reviews-track"
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-6 px-6 lg:mx-0 lg:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {shown.map((r, i) => {
              const isOpen = expanded[i];
              return (
                <figure
                  key={r.id}
                  data-testid={`review-${i}`}
                  className="snap-start flex-shrink-0 w-[85%] sm:w-[380px] p-8 bg-white text-slate-900 rounded-2xl shadow-xl shadow-black/20 flex flex-col"
                >
                  <div className="flex items-start justify-between">
                    <Quote className="w-6 h-6 text-blue-600/40" />
                    {r.source === "Google" && (
                      <div className="flex items-center gap-1.5 text-xs text-slate-500">
                        <GoogleLogo className="w-4 h-4" />
                        <span>Google</span>
                      </div>
                    )}
                  </div>
                  <div className="flex mt-4">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star
                        key={s}
                        className={`w-4 h-4 ${s < r.rating ? "fill-[#EAB308] text-[#EAB308]" : "text-slate-300"}`}
                      />
                    ))}
                  </div>
                  <blockquote className={`mt-4 text-slate-700 leading-relaxed ${isOpen ? "" : "line-clamp-4"}`}>
                    « {r.text} »
                  </blockquote>
                  <button
                    data-testid={`review-more-${i}`}
                    onClick={() => setExpanded((prev) => ({ ...prev, [i]: !isOpen }))}
                    className="mt-2 self-start text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    {isOpen ? t("reviews.seeLess") : t("reviews.seeMore")}
                  </button>
                  <figcaption className="mt-auto flex items-center gap-3 pt-6 border-t border-slate-100">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-600 to-slate-800 text-white grid place-items-center font-display text-sm font-bold">
                      {initials(r.name)}
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">{r.name}</div>
                      {r.city && <div className="text-xs text-slate-500">{r.city}</div>}
                    </div>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
